import type { CSSProperties } from 'react';

/** State a page keeps for the shared cell right-click menu. `field` is the
 *  right-clicked column; `rowId` the source row. Keep one of these per grid page. */
export interface CellMenuState {
  visible: boolean;
  x: number;
  y: number;
  rowId: string | number | null;
  field: string | null;
}

export const CELL_MENU_CLOSED: CellMenuState = { visible: false, x: 0, y: 0, rowId: null, field: null };

const itemStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
  textAlign: 'left', color: '#1f2430',
};
const iconStyle: CSSProperties = { fontSize: 14, color: '#6b7280' };
const sepStyle: CSSProperties = { height: 1, background: '#e5e7eb', margin: '0 8px' };

/**
 * Shared grid cell right-click context menu: Refresh · Copy to All Cells · Copy to
 * Selected Rows. Ported from legacy ALPACA's grid "Refresh" + "Copy To All Cell"
 * (FieldDataEntryViewController / SampleTransferViewController GridView_FillContextMenuItems);
 * "Copy to Selected Rows" is the AI extension matching legacy Sample Transfer, which copies
 * the focused cell only to the SELECTED rows. Used by Field Data Entry and Sample Transfer.
 * The page owns the state + the copy/refresh handlers (they touch its own rows/commit).
 */
export function CellCopyMenu({
  menu, onClose, onRefresh, onCopyAll, onCopySelected, copyAllLabel = 'Copy to All Cells',
}: {
  menu: CellMenuState;
  onClose: () => void;
  onRefresh: () => void;
  onCopyAll: () => void;
  /** Omit to hide "Copy to Selected Rows" — G5's Pre-Login Samples grid offers only
   *  Refresh + Copy To All Cell (which itself copies to the ticked rows). */
  onCopySelected?: () => void;
  /** The caption of the copy action (default "Copy to All Cells"). */
  copyAllLabel?: string;
}) {
  if (!menu.visible) return null;
  return (
    <>
      {/* Backdrop — a click or right-click anywhere outside closes the menu. */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
        onClick={onClose}
        onContextMenu={e => { e.preventDefault(); onClose(); }}
      />
      <div style={{
        position: 'fixed', left: menu.x, top: menu.y, zIndex: 9999,
        background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
        boxShadow: '0 4px 16px rgba(0,0,0,0.13)', minWidth: 190, overflow: 'hidden',
        fontSize: 13, fontFamily: 'inherit',
      }}>
        <button style={itemStyle}
          onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          onClick={onRefresh}>
          <i className="ti ti-refresh" style={iconStyle} />
          Refresh
        </button>
        <div style={sepStyle} />
        <button style={itemStyle}
          onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          onClick={onCopyAll}>
          <i className="ti ti-copy" style={iconStyle} />
          {copyAllLabel}
        </button>
        {onCopySelected && (<>
        <div style={sepStyle} />
        <button style={itemStyle}
          onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          onClick={onCopySelected}>
          <i className="ti ti-checkbox" style={iconStyle} />
          Copy to Selected Rows
        </button>
        </>)}
      </div>
    </>
  );
}
